import React, { useRef, useState } from 'react';
import { formatPrice, getOrderStatusColor } from '../utils/helpers';
import { BiChevronDown } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { useUserContext } from '../context/user_context';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  SimpleGrid,
  VStack,
  Text,
  Spinner,
  HStack,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useOrderContext } from '../context/order_context';

function OrdersTable({ orders }) {
  const toast = useToast();
  const { currentUser } = useUserContext();
  const { fetchOrders, deleteOrder } = useOrderContext();
  const [loading, setLoading] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const confirmDelete = (id) => {
    setOrderToDelete(id);
    onOpen();
  };

  const handleDelete = async () => {
    const id = orderToDelete;
    onClose();
    setLoading(true);
    const response = await deleteOrder(id);
    setLoading(false);
    if (response.success) {
      toast({
        position: 'top',
        description: response.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return await fetchOrders();
    } else {
      return toast({
        position: 'top',
        description: response.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Sort orders by date (newest first)
  const sortedOrders = [...orders].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <SimpleGrid bg='white' p={5} shadow='lg' borderRadius='lg' overflowX='auto'>
      {loading ? (
        <HStack my={8} alignItems='center' justifyContent='center'>
          <Spinner size='lg' color='brown.500' />
        </HStack>
      ) : (
        <Table variant='simple'>
          <Thead>
            <Tr>
              <Th>Customer</Th>
              <Th>Items</Th>
              <Th>Date</Th>
              <Th>Status</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedOrders.map((order, index) => {
              const {
                user: { name },
                orderItems,
                orderStatus,
                createdAt,
                _id: id,
                itemsPrice,
                shippingPrice,
                totalPrice,
              } = order;

              const orderItemsPrice = Number(itemsPrice) || 0;
              const orderShippingPrice = Number(shippingPrice) || 0;
              const orderTotalPrice =
                typeof totalPrice === 'number' && !Number.isNaN(totalPrice)
                  ? totalPrice
                  : orderItemsPrice + orderShippingPrice;

              return (
                <Tr key={index}>
                  <Td>{name}</Td>
                  <Td>
                    <VStack alignItems='flex-start' spacing={5}>
                      {orderItems.map((item, index) => {
                        const { image, name, price } = item;
                        return (
                          <HStack key={index}>
                            <Image
                              src={image}
                              boxSize='50px'
                              objectFit='cover'
                              borderRadius='lg'
                              flexShrink={0}
                            />
                            <VStack alignItems='flex-start' spacing={0}>
                              <Text as='b' fontSize='sm' noOfLines={2}>{name.substring(0, 18)}...</Text>
                              <Text fontSize='xs' color='green.500' whiteSpace='nowrap'>
                                {formatPrice(price)} x{item.quantity || 1} = <Text as='span' color='blue.500' fontWeight='bold'>{formatPrice(price * (item.quantity || 1))}</Text>
                              </Text>
                            </VStack>
                          </HStack>
                        );
                      })}
                      <VStack alignItems='flex-start' spacing={0} pt={1}>
                        <Text fontSize='xs' color='gray.600'>
                          Shipping: {orderShippingPrice === 0 ? 'FREE' : formatPrice(orderShippingPrice)}
                        </Text>
                        <Text fontSize='sm' color='green.600' fontWeight='bold'>
                          Total: {formatPrice(orderTotalPrice)}
                        </Text>
                      </VStack>
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize='sm' color='gray.600'>
                      {formatDate(createdAt)}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={getOrderStatusColor(orderStatus)}>
                      {orderStatus}
                    </Badge>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} rightIcon={<BiChevronDown />}>
                        Actions
                      </MenuButton>
                      <MenuList>
                        <Link to={`/orders/${id}`}>
                          <MenuItem>View</MenuItem>
                        </Link>
                        {currentUser.privilege !== 'low' && (
                          <MenuItem onClick={() => confirmDelete(id)}>
                            Delete
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Delete Order
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this order? This action cannot
              be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme='red' onClick={handleDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </SimpleGrid>
  );
}

export default OrdersTable;
